<template>
  <div v-if="!isReAskScreenVisible">
    <FormHeader :formStep="formStep" :showBackBtn="false" />
    <FormContainer :class="$style.wrapper">
      <TransitionWrapper>
        <div :class="$style.codeWrapper">
          <VerificationCode
            :code="verificationCode"
            :label="t('smsConfirmation.codeLabel')"
            @input="updateVerificationCode"
            @keyup.enter="handleEnter"
          >
            <template #errorMessages>
              <p v-if="isErrorMessageShow">{{ t('errorMessage.invalidCode') }}</p>
            </template>
          </VerificationCode>
          <p :class="$style.description">
            <span v-html="t('smsConfirmation.description')" />&nbsp;<label
              :class="$style.linkText"
              @click="handleHereClick"
              >{{ t('linkText.link') }}</label
            >.
          </p>
        </div>
        <FormBottom
          :onNext="handleNext"
          :nextDisabled="isRequestLoading"
          :nextButtonLabel="t('button.confirm')"
        />
      </TransitionWrapper>
    </FormContainer>
  </div>
  <PhoneNumber
    v-else
    :title="t('numberVerification.titleForReAsk')"
    :backButtonVisible="false"
    :descriptionVisible="false"
    :onSubmitSuccess="handleReAskSuccess"
  />
  <ToastMessage
    v-if="toastType !== ToastType.AddSuccess"
    :notificationWidth="BREAKPOINTS.funnel_container_max_width.toString() + 'px'"
    data-testid="otp-notification"
  />
</template>

<script lang="ts" setup>
import { storeToRefs } from 'pinia';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import {
  FormBottom,
  FormContainer,
  FormHeader,
  TransitionWrapper,
  VerificationCode,
} from '@/components/common';
import ToastMessage from '@/components/common/ToastMessage/ToastMessage.vue';
import PhoneNumber from '@/components/property-valuation-page/PhoneNumber';
import { useResultNavigation } from '@/composables/useResultNavigation';
import { useTrackingWithTreatment } from '@/composables/useTrackingWithTreatment';
import { OfferType } from '@/models/valuationFunnel';
import { usePropertyDetailsStore } from '@/stores/propertyDetails/propertyDetails';
import { usePropertyValuationStore, verifyOTP } from '@/stores/propertyValuation';
import { trackingSellerLeadSubmit } from '@/tracking/tracking';
import { BREAKPOINTS, ToastType } from '@/types';

const MAX_REASK_CODE_TIME = 3;
const CODE_DIGITS = 5;

const propertyValuationStore = usePropertyValuationStore();
const { data: formData, step: formStep } = storeToRefs(propertyValuationStore);
const propertyDetailsStore = usePropertyDetailsStore();
const { t, locale } = useI18n();
const { trackVirtualPageViewWithTreatment } = useTrackingWithTreatment();
const { handleRoute, getStepInputsForTracking, setToastType } = useResultNavigation();

// Reactive data
const verificationCode = ref<string | undefined>(undefined);
const verificationCodeHistory = ref<string[]>([]);
const isReAskScreenVisible = ref(false);
const reAskedCount = ref(0);
const isErrorMessageShow = ref(false);
const isRequestLoading = ref(false);

// Computed properties
const toastType = computed(() => propertyDetailsStore.toastType);

// Methods
const getVerificationCodeRepeat = (code: string): boolean => {
  const targetCode = verificationCodeHistory.value.find((value) => value === code);
  return !!targetCode;
};

const updateVerificationCode = (newCode: any): void => {
  verificationCode.value = newCode.target.value;
};

const handleHereClick = (): void => {
  if (reAskedCount.value < MAX_REASK_CODE_TIME) {
    isReAskScreenVisible.value = true;
  } else {
    setToastType(ToastType.OtpLimitReached);
  }
};

const handleNext = async (): Promise<void> => {
  const { leadId = '', offerType } = formData.value;
  const otpCode = verificationCode.value;

  if (verificationCode.value?.length !== CODE_DIGITS) {
    isErrorMessageShow.value = true;
    return;
  } else {
    isErrorMessageShow.value = false;
  }

  if (isRequestLoading.value) {
    return;
  }

  if (getVerificationCodeRepeat(otpCode || '')) {
    setToastType(ToastType.OtpLimitReached);
    return;
  }

  verificationCodeHistory.value.push(otpCode || '');

  isRequestLoading.value = true;
  const response = await verifyOTP({
    leadId: leadId,
    otpCode: otpCode || '',
  });
  isRequestLoading.value = false;

  if (response.isSuccess) {
    if (offerType === OfferType.Selling) {
      trackingSellerLeadSubmit(leadId);
    }

    await handleRoute(leadId);
  } else {
    isErrorMessageShow.value = true;
  }
};

const handleEnter = (): void => {
  handleNext();
};

const handleReAskSuccess = (): void => {
  reAskedCount.value++;
  isReAskScreenVisible.value = false;
  setToastType(ToastType.OtpSuccess);
  verificationCode.value = undefined;
  isErrorMessageShow.value = false;
};

// Lifecycle
onMounted(() => {
  setToastType(ToastType.OtpSuccess);
  trackVirtualPageViewWithTreatment({
    subType: 'input_verificationCode',
    locale: locale.value,
    stepInputs: getStepInputsForTracking(),
  });
});
</script>

<style lang="scss" module>
@use '@/assets/variables' as *;

.wrapper {
  padding-top: $space-250;
}

.notification {
  margin-bottom: $space-150;
}

.codeWrapper {
  max-width: 306px;
  margin: 0 auto;
}

.description {
  margin-top: $space-200;
  margin-bottom: 0;

  @include body-3($color-black);
}

.linkText {
  display: inline-flex;
  align-items: center;
  color: $color-azul-110;
  cursor: pointer;
  -webkit-box-align: center;

  &:hover {
    text-decoration: underline;
    cursor: pointer;
  }
}

.hintNotification {
  margin-top: $space-200;
  margin-bottom: 0;
}
</style>
